import React, { useCallback, useEffect, useRef } from 'react';

import Markdown from 'markdown-to-jsx';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AIAwareness, AIDisclaimer, AIFeedbackComponent } from '@atlassian/ai-components';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

const MAU_CRITERIA_DWELL_TIME = 3000;

const sanitizeMarkdown = (text: string) => {
	if (!text) {
		return '';
	}
	return text
		.replace(/={4,}/g, '===') // h1 headers
		.replace(/-{4,}/g, '---'); // h2 headers
};

type Props = {
	contentId: string;
	isHidden?: boolean;
	maxLines: number;
	renderAsMarkdown?: boolean;
	showAwareness?: boolean;
	showFooter?: boolean;
	source?: string;
	text?: string;
};

export const AISnippetContent = ({
	text,
	contentId,
	showFooter = true,
	showAwareness = false,
	source,
	isHidden,
	maxLines,
	renderAsMarkdown = true,
}: Props): React.JSX.Element => {
	const ref = useRef<HTMLDivElement>(null);
	const { getDwellTime } = usePopupDwellTime(ref, 250);
	const { trackAIResultView, trackAIFeedbackSubmit } = useAIEventsInstrumentation();
	// This is needed to keep the snippet value in scope when running the unmount effect
	const snippetValueRef = useRef(text);

	const isExperimentEnabled = FeatureGates.getExperimentValue(
		'cc-ai-snippet-fixes-1025',
		'isEnabled',
		false,
	);
	const handleLikeClicked = useCallback(() => {
		trackAIFeedbackSubmit('up');
	}, [trackAIFeedbackSubmit]);

	const handleDislikeClicked = useCallback(() => {
		trackAIFeedbackSubmit('down');
	}, [trackAIFeedbackSubmit]);

	useEffect(() => {
		const currentText = snippetValueRef.current;

		return () => {
			const dwellTime = getDwellTime();
			if (!isHidden) {
				if (showFooter && currentText && dwellTime >= MAU_CRITERIA_DWELL_TIME) {
					trackAIResultView({ attributes: { dwellTime, source, wasShownAIContent: true } });
				} else {
					trackAIResultView({
						attributes: { dwellTime, source, wasShownAIContent: showFooter && !!currentText },
						doesNotMeetMAUCriteria: true,
					});
				}
			}
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Stack space="space.100" ref={ref}>
			{showAwareness && <AIAwareness />}
			<Text size="small" maxLines={maxLines}>
				{renderAsMarkdown ? (
					isExperimentEnabled ? (
						<Markdown options={{ disableParsingRawHTML: true }}>
							{sanitizeMarkdown(text || '')}
						</Markdown>
					) : (
						<Markdown>{text || ''}</Markdown>
					)
				) : (
					text || ''
				)}
			</Text>
			{showFooter && text && (
				<Inline space="space.100">
					<AIDisclaimer />
					<AIFeedbackComponent
						contentId={contentId}
						onLikeClicked={handleLikeClicked}
						onDislikeClicked={handleDislikeClicked}
					/>
				</Inline>
			)}
		</Stack>
	);
};
